/**
 * Spec 040: how a staff member's feed reads — its events in order, how long it waits for people,
 * the files of its archive. No React and no import here: the tests load it as it is.
 */

export interface WithId {
  id: number;
}

/** The events known and those that came, each once, oldest first. */
export function mergeEvents<T extends WithId>(known: T[], came: T[]): T[] {
  const byId = new Map<number, T>();
  for (const e of known) byId.set(e.id, e);
  for (const e of came) byId.set(e.id, e);
  return [...byId.values()].sort((a, b) => a.id - b.id);
}

/** «меньше минуты», «37 мин», «2 ч 5 мин», «3 дн». */
export function waited(ms: number): string {
  const minutes = Math.floor(Math.max(0, ms) / 60_000);
  if (minutes < 1) return "меньше минуты";
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours} ч ${minutes % 60} мин` : `${hours} ч`;
  return `${Math.floor(hours / 24)} дн`;
}

/** What a staff member waits for from people, as «ждёт …» says it. */
export const WAITS_FOR: Record<string, string> = {
  permission: "разрешения",
  question: "ответа",
  right: "права",
  limit: "продления лимита",
  second_window: "решения",
};

/** «ждёт разрешения 37 мин». */
export function waitsLine(kind: string, since: string, now: number = Date.now()): string {
  return `ждёт ${WAITS_FOR[kind] ?? "решения"} ${waited(now - Date.parse(since))}`;
}

const day = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/** «12.10–15.10», or one day: «12.10». */
export function period(first: string, last: string): string {
  const a = day(first);
  const b = day(last);
  return a === b ? a : `${a}–${b}`;
}

/** «900 байт», «340 КиБ», «1,2 МиБ». */
export function sizeWords(bytes: number): string {
  if (bytes < 1024) return `${bytes} байт`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КиБ`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} МиБ`;
}

function plural(n: number, one: string, few: string, many: string): string {
  const tens = n % 100;
  const units = n % 10;
  if (tens >= 11 && tens <= 14) return many;
  if (units === 1) return one;
  if (units >= 2 && units <= 4) return few;
  return many;
}

/** «Архив: 12.10–15.10 · 340 КиБ · 1 200 событий». */
export function archiveLine(a: { first_at: string; last_at: string; size: number; events: number }): string {
  return `Архив: ${period(a.first_at, a.last_at)} · ${sizeWords(a.size)} · ${a.events.toLocaleString("ru-RU")} ${plural(a.events, "событие", "события", "событий")}`;
}

/** «07:31» — the time of an event, by the reader's clock. */
export function clock(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** A kind of event in a word, next to its line. */
export const KIND_LABEL: Record<string, string> = {
  turn: "ход",
  said: "сказал",
  tool: "инструмент",
  result: "результат",
  tasker: "таскер",
  helper: "помощник",
  console: "консоль",
  waits: "ждёт",
  answer: "ответ",
  wrote: "написали",
  control: "управление",
  push: "напоминание",
};

/** A line of the feed that matters to people: what waits, answers, messages, control, failures. */
export function isLoud(e: { kind: string; payload: Record<string, unknown> }): boolean {
  if (["waits", "answer", "wrote", "control", "push"].includes(e.kind)) return true;
  return e.kind === "result" && e.payload.ok === false;
}
